import { useCallback, useEffect, useMemo, useState } from "react";
import { Save, X, Search } from "lucide-react";
import { toast } from "../../lib/notify";
import { http, toastApiError } from "../../lib/api";
import { trUpper } from "../../lib/uppercase";
import AmountInput, { parseTL } from "../AmountInput";
import { AYLAR, fmtIn } from "./dailyBudgetCalc";

const EMPTY_CELL = { income: "", expense: "", day: "" };

const nextOnEnter = (e) => {
  if (e.key !== "Enter") return;
  e.preventDefault();
  const inputs = [...document.querySelectorAll("[data-testid=plan-panel] [data-testid^='plan-cell-']")];
  const next = inputs[inputs.indexOf(e.currentTarget) + 1];
  if (next) { next.focus(); next.select?.(); }
};

function PlanRow({ leaf, parentName, cell, isDirty, dayList, onCell, onNorm }) {
  const cls = (f) => `px-1.5 py-1 text-[11px] border rounded outline-none focus:border-amber-500 ${isDirty ? "border-amber-400 bg-amber-50" : "border-slate-200"} ${f}`;
  return (
    <tr className="border-t border-slate-100" data-testid={`plan-row-${leaf.id}`}>
      <td className="px-2 py-1 text-xs font-medium whitespace-nowrap sticky left-0 bg-white">
        {leaf.name}
        {parentName && <span className="block text-[9px] text-slate-400">{parentName}</span>}
      </td>
      <td className="px-1 py-0.5 text-right"><AmountInput value={cell.income} onChange={(v) => onCell("income", v)} onKeyDown={nextOnEnter} onBlur={() => onNorm("income")} testid={`plan-cell-${leaf.id}-income`} placeholder="-" className={cls("w-32 text-right")} /></td>
      <td className="px-1 py-0.5 text-right"><AmountInput value={cell.expense} onChange={(v) => onCell("expense", v)} onKeyDown={nextOnEnter} onBlur={() => onNorm("expense")} testid={`plan-cell-${leaf.id}-expense`} placeholder="-" className={cls("w-32 text-right")} /></td>
      <td className="px-1 py-0.5 text-center">
        <select value={cell.day} onChange={(e) => onCell("day", e.target.value)} onKeyDown={nextOnEnter} data-testid={`plan-cell-${leaf.id}-day`} className={cls("w-20 bg-white text-center")}>
          <option value="">Gün…</option>
          {dayList.map((d) => <option key={d} value={Number(d)}>{Number(d)}. gün</option>)}
        </select>
      </td>
    </tr>
  );
}

// Aylık plan girişi paneli (planlanan gelir / gider / gün) — yalnız açıkken render edilir
export default function PlanEntryPanel({ month, y, m, dayCount, dayList, leafs, byId, onClose, onSaved }) {
  const [cells, setCells] = useState({}); // {itemId: {income, expense, day}}
  const [dirty, setDirty] = useState({});
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");

  useEffect(() => {
    http.get(`/budget-plans?year=${y}`).then((r) => {
      const c = {};
      r.data.filter((p) => p.month === m).forEach((p) => {
        const inc = p.planned_income || 0, exp = p.planned_expense || (!p.planned_income ? p.amount : 0) || 0;
        c[p.budget_item_id] = { income: fmtIn(inc), expense: fmtIn(exp), day: p.planned_day ? String(p.planned_day) : "" };
      });
      setCells(c); setDirty({});
    }).catch((e) => toastApiError(e));
  }, [y, m]);

  const cancel = useCallback(() => { setDirty({}); onClose(); }, [onClose]);
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") { e.stopPropagation(); cancel(); } };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [cancel]);

  const setCell = (itemId, field, val) => {
    setCells((c) => ({ ...c, [itemId]: { ...(c[itemId] || EMPTY_CELL), [field]: val } }));
    setDirty((d) => ({ ...d, [itemId]: true }));
  };
  const normCell = (itemId, f) => { const n = parseTL(cells[itemId]?.[f] || ""); if (n !== null) setCells((cc) => ({ ...cc, [itemId]: { ...(cc[itemId] || {}), [f]: fmtIn(n) } })); };
  const save = async () => {
    const rows = Object.keys(dirty).map((itemId) => {
      const c = cells[itemId] || {};
      const day = c.day ? Number(c.day) : null;
      return { budget_item_id: itemId, planned_income: parseTL(c.income || "") ?? 0, planned_expense: parseTL(c.expense || "") ?? 0, planned_day: day && day >= 1 && day <= dayCount ? day : null };
    });
    if (!rows.length) return toast.info("Değişiklik yok");
    setSaving(true);
    try {
      await http.post("/budget-plans/bulk", { year: y, month: m, rows });
      toast.success(`${AYLAR[m - 1]} ${y} planı kaydedildi`);
      setDirty({});
      onSaved();
    } catch (e) { toastApiError(e); }
    setSaving(false);
  };

  const visibleLeafs = useMemo(() => {
    const q = trUpper(search.trim());
    return q ? leafs.filter((l) => trUpper(`${l.name} ${byId[l.parent_id]?.name || ""}`).includes(q)) : leafs;
  }, [leafs, byId, search]);

  return (
    <div className="border-b border-slate-200 p-4 space-y-3 bg-slate-50/50" data-testid="plan-panel">
      <div className="flex items-center gap-3 flex-wrap">
        <span className="text-xs font-semibold text-slate-700">Aylık Plan Girişi — <b>{AYLAR[m - 1]} {y}</b> (ay yukarıdaki ay seçiciden değişir)</span>
        <span className="text-[10px] text-slate-500">Planlanan gün: tutarın o ay hangi gün gerçekleşmesi bekleniyor. Yıllık plan = aylık planların toplamı (manuel girilmez).</span>
        <button onClick={save} disabled={saving} data-testid="plan-save" className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-slate-900 text-white text-xs font-semibold hover:bg-slate-800 disabled:opacity-60">
          <Save className="w-3.5 h-3.5" /> {saving ? "Kaydediliyor…" : "Planı Kaydet"}
        </button>
        <button type="button" onClick={cancel} data-testid="plan-cancel" title="Kaydetmeden kapat (ESC)" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-slate-300 bg-white text-slate-700 text-xs font-semibold hover:bg-slate-100">
          <X className="w-3.5 h-3.5" /> İptal
        </button>
      </div>
      <div className="overflow-x-auto">
        <table className="tbl text-[11px]">
          <thead>
            <tr>
              <th className="text-left px-2 py-1.5 text-[10px] uppercase tracking-wider text-slate-600 font-semibold sticky left-0 bg-white">
                <div className="flex flex-col gap-1">
                  <span>Alt Bütçe Kalemi</span>
                  <div className="relative">
                    <Search className="w-3 h-3 absolute left-1.5 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Kalem ara…" data-testid="plan-search"
                      className="w-44 pl-6 pr-5 py-1 text-[11px] normal-case tracking-normal font-normal border border-slate-300 rounded outline-none focus:border-amber-500 bg-white" />
                    {search && <button type="button" onClick={() => setSearch("")} className="absolute right-1 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700" data-testid="plan-search-clear"><X className="w-3 h-3" /></button>}
                  </div>
                </div>
              </th>
              <th className="text-right px-2 py-1.5 text-[10px] uppercase tracking-wider text-emerald-700 font-semibold">Planlanan Gelir</th>
              <th className="text-right px-2 py-1.5 text-[10px] uppercase tracking-wider text-red-700 font-semibold">Planlanan Gider</th>
              <th className="text-center px-2 py-1.5 text-[10px] uppercase tracking-wider text-slate-600 font-semibold">Planlanan Gün</th>
            </tr>
          </thead>
          <tbody>
            {visibleLeafs.map((l) => (
              <PlanRow key={l.id} leaf={l} parentName={l.parent_id && byId[l.parent_id] ? byId[l.parent_id].name : ""} cell={cells[l.id] || EMPTY_CELL} isDirty={!!dirty[l.id]} dayList={dayList}
                onCell={(f, v) => setCell(l.id, f, v)} onNorm={(f) => normCell(l.id, f)} />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
